(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let listenersReady = false;
  let lastDialogTrigger = null;
  let journalOpening = false;
  let gamePortalOpening = false;
  let lockedScrollPosition = 0;
  let cartTask = Promise.resolve();
  let pendingCartTasks = 0;
  const protectionPricingConfigured = true;
  let protectionVariantSource = null;
  let protectionVariants = [];

  if (window.location.pathname === '/policies/terms-of-service') {
    const policyTitle = document.querySelector('.shopify-policy__title h1');
    if (policyTitle) policyTitle.textContent = 'Terms of Service';
  }

  const openDialogSelector = '.bts-product-panel[open], .bts-cart-drawer[open]';

  const lockPageScroll = () => {
    if (root.classList.contains('bts-dialog-open')) return;
    lockedScrollPosition = window.scrollY;
    root.classList.add('bts-dialog-open');
    document.body.style.top = `-${lockedScrollPosition}px`;
  };

  const unlockPageScroll = () => {
    if (document.querySelector(openDialogSelector) || !root.classList.contains('bts-dialog-open')) return;
    root.classList.remove('bts-dialog-open');
    document.body.style.removeProperty('top');
    window.scrollTo(0, lockedScrollPosition);
  };

  const showBtsDialog = dialog => {
    if (!dialog) return;
    lockPageScroll();
    if (dialog.open) return;
    try {
      dialog.showModal();
    } catch (error) {
      unlockPageScroll();
      throw error;
    }
  };

  const syncDialogScrollLock = () => {
    if (document.querySelector(openDialogSelector)) lockPageScroll();
    else unlockPageScroll();
  };

  const openJournal = (enter, target) => {
    if (journalOpening) return;
    const cover = enter.closest('.bts-threshold')?.querySelector('[data-bts-journal-cover]');
    const goToPage = () => {
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
      if (window.location.hash !== '#bts-story') history.pushState(null, '', '#bts-story');
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };
    if (!cover || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      goToPage();
      return;
    }

    journalOpening = true;
    const bounds = cover.getBoundingClientRect();
    const overlay = document.createElement('div');
    overlay.className = 'bts-journal bts-journal-transition';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.inert = true;
    const leaf = document.createElement('div');
    leaf.className = 'bts-journal__leaf';
    const copy = cover.cloneNode(true);
    copy.removeAttribute('data-bts-journal-cover');
    copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
    [leaf, copy].forEach(element => {
      Object.assign(element.style, {
        left: `${bounds.left}px`, top: `${bounds.top}px`,
        width: `${bounds.width}px`, height: `${bounds.height}px`
      });
      overlay.append(element);
    });
    document.body.append(overlay);
    // Scroll beneath the cover so the real dictionary page is revealed by the turn.
    goToPage();
    let fallback;
    const finish = () => {
      clearTimeout(fallback);
      overlay.remove();
      journalOpening = false;
      document.removeEventListener('keydown', dismiss);
    };
    const dismiss = event => { if (event.key === 'Escape') finish(); };
    leaf.addEventListener('animationend', finish, { once: true });
    document.addEventListener('keydown', dismiss);
    // Also clean up if an animation is cancelled by a theme-editor refresh.
    fallback = setTimeout(finish, 1500);
  };

  const openGamePortal = link => {
    if (gamePortalOpening) return;
    if (reducedMotion) {
      window.location.assign(link.href);
      return;
    }

    gamePortalOpening = true;
    const overlay = document.createElement('div');
    overlay.className = 'bts-game-portal';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.inert = true;
    const bookmark = link.getBoundingClientRect();
    overlay.style.setProperty('--bts-portal-x', `${bookmark.left + bookmark.width / 2}px`);
    overlay.style.setProperty('--bts-portal-y', `${bookmark.top + bookmark.height / 2}px`);

    // Curved radial paths make a real spiral rather than straight rotating rays.
    const spiralPath = offset => {
      const points = [];
      for (let radius = 14; radius <= 710; radius += 10) {
        const angle = offset + radius / 79;
        points.push(`${(500 + Math.cos(angle) * radius).toFixed(1)},${(500 + Math.sin(angle) * radius).toFixed(1)}`);
      }
      return `M ${points.join(' L ')}`;
    };
    const arms = Array.from({ length: 8 }, (_, index) => {
      const path = spiralPath(index * Math.PI / 4);
      return `<path class="bts-game-portal__trail" d="${path}"/><path class="bts-game-portal__highlight" d="${path}"/>`;
    }).join('');
    overlay.innerHTML = `<svg class="bts-game-portal__spiral" viewBox="0 0 1000 1000" aria-hidden="true" focusable="false"><g>${arms}</g></svg><span class="bts-game-portal__eye"></span>`;
    document.body.append(overlay);

    let navigated = false;
    const navigate = () => {
      if (navigated) return;
      navigated = true;
      window.location.assign(link.href);
    };
    overlay.addEventListener('animationend', event => {
      if (event.target === overlay) navigate();
    });
    // Navigation still works if the browser suppresses animation events.
    window.setTimeout(navigate, 1550);
  };

  const money = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const routesRoot = () => window.Shopify?.routes?.root || '/';

  const fetchCart = async () => {
    const response = await fetch(`${routesRoot()}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  };

  const protectionMeta = () => {
    const control = document.querySelector('[data-bts-cart-protection]');
    if (!control) return null;
    const productId = Number(control.dataset.protectionProductId);
    const source = document.querySelector('[data-bts-protection-variants]');
    if (source !== protectionVariantSource) {
      protectionVariantSource = source;
      try {
        protectionVariants = JSON.parse(source?.textContent || '[]').filter(variant =>
          variant.available && Number.isSafeInteger(variant.id) && Number.isSafeInteger(variant.price) && variant.price > 0
        );
      } catch (_error) {
        protectionVariants = [];
      }
    }
    return productId ? { productId, variants: protectionVariants } : null;
  };

  const protectionLines = (cart, productId) => cart.items.filter(item => Number(item.product_id) === productId);
  const hasMerchandise = (cart, productId) => cart.items.some(item => Number(item.product_id) !== productId);
  const protectionSelected = cart => cart.attributes?.['Package Protection'] !== 'No';
  const merchandiseSubtotal = (cart, productId) => cart.items.reduce((total, item) =>
    total + (Number(item.product_id) === productId ? 0 : item.final_line_price), 0);
  const closestProtectionVariant = (cart, meta) => {
    if (!meta?.variants.length) return null;
    const targetPrice = Math.round(merchandiseSubtotal(cart, meta.productId) * .04);
    return meta.variants.reduce((closest, variant) => {
      if (!closest) return variant;
      const difference = Math.abs(variant.price - targetPrice) - Math.abs(closest.price - targetPrice);
      return difference < 0 || (difference === 0 && variant.price < closest.price) ? variant : closest;
    }, null);
  };

  const postCart = async (path, body) => {
    const response = await fetch(`${routesRoot()}cart/${path}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'The field bag could not be updated.');
    return data;
  };

  const reconcileProtection = async cart => {
    if (!protectionPricingConfigured) return { cart, changed: false };
    const meta = protectionMeta();
    if (!meta) return { cart, changed: false };

    const lines = protectionLines(cart, meta.productId);
    const merchandise = hasMerchandise(cart, meta.productId);
    const selected = protectionSelected(cart);
    const variant = merchandise && selected ? closestProtectionVariant(cart, meta) : null;
    const updates = {};

    if (!merchandise || !selected || !variant) {
      lines.forEach(item => { updates[item.key] = 0; });
      const resetPreference = !merchandise && !selected;
      if (lines.length || resetPreference) {
        cart = await postCart('update', {
          ...(lines.length ? { updates } : {}),
          ...(resetPreference ? { attributes: { 'Package Protection': 'Yes' } } : {})
        });
        return { cart, changed: true };
      }
      return { cart, changed: false };
    }

    const matchingLine = lines.find(item => Number(item.variant_id) === variant.id);
    lines.forEach(item => {
      const quantity = item === matchingLine ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!matchingLine) updates[variant.id] = 1;
    if (Object.keys(updates).length) {
      return { cart: await postCart('update', { updates }), changed: true };
    }
    return { cart, changed: false };
  };

  const syncCartBusy = () => {
    document.querySelectorAll('[data-bts-protection-checkbox]').forEach(checkbox => {
      checkbox.disabled = pendingCartTasks > 0;
    });
    document.querySelectorAll('[data-bts-cart-checkout-form]').forEach(syncCartTerms);
  };

  const queueCartTask = task => {
    pendingCartTasks += 1;
    syncCartBusy();
    const result = cartTask.then(task, task);
    cartTask = result.catch(() => {});
    return result.finally(() => {
      pendingCartTasks -= 1;
      syncCartBusy();
    });
  };

  const reloadFullCartIfChanged = changed => {
    if (changed && window.location.pathname.replace(/\/$/, '') === `${routesRoot()}cart`.replace(/\/$/, '')) {
      window.location.reload();
    }
  };

  const updateCartCount = count => {
    document.querySelectorAll('[data-bts-cart-count]').forEach(counter => {
      counter.textContent = count;
      counter.hidden = count === 0;
    });
    document.querySelectorAll('[data-bts-cart-link]').forEach(link => {
      link.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  };

  const makeCartItem = (item, currency) => {
    const article = document.createElement('article');
    article.className = 'bts-cart-item';
    const meta = protectionMeta();
    const isProtection = meta && Number(item.product_id) === meta.productId;
    if (isProtection) article.classList.add('bts-cart-item--protection');

    if (item.featured_image?.url && !isProtection) {
      const image = document.createElement('img');
      image.src = item.featured_image.url;
      image.alt = item.featured_image.alt || item.product_title;
      image.width = 120;
      image.height = 150;
      article.append(image);
    }

    const details = document.createElement('div');
    details.className = 'bts-cart-item__details';

    const title = document.createElement('a');
    title.href = item.url;
    title.textContent = item.product_title;
    details.append(title);

    if (item.variant_title && !isProtection) {
      const variant = document.createElement('p');
      variant.textContent = item.variant_title;
      details.append(variant);
    }

    const price = document.createElement('p');
    price.className = 'bts-cart-item__price';
    price.textContent = money(item.final_line_price, currency);
    details.append(price);

    if (!isProtection) {
      const controls = document.createElement('div');
      controls.className = 'bts-cart-item__controls';

      const label = document.createElement('label');
      label.textContent = 'QTY';
      label.setAttribute('for', `BtsCartQuantity-${item.key.replace(/[^a-zA-Z0-9]/g, '')}`);

      const quantity = document.createElement('input');
      quantity.id = label.htmlFor;
      quantity.type = 'number';
      quantity.min = '0';
      quantity.value = item.quantity;
      quantity.setAttribute('data-bts-cart-quantity', '');
      quantity.dataset.lineKey = item.key;

      const update = document.createElement('button');
      update.type = 'button';
      update.textContent = 'UPDATE';
      update.setAttribute('data-bts-cart-update', '');
      update.dataset.lineKey = item.key;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = 'REMOVE';
      remove.setAttribute('data-bts-cart-remove', '');
      remove.dataset.lineKey = item.key;

      controls.append(label, quantity, update, remove);
      details.append(controls);
    }
    article.append(details);
    return article;
  };

  const renderCart = cart => {
    const drawer = document.querySelector('[data-bts-cart-drawer]');
    if (!drawer) return;
    const items = drawer.querySelector('[data-bts-cart-items]');
    const total = drawer.querySelector('[data-bts-cart-total]');
    const checkoutForm = drawer.querySelector('[data-bts-cart-checkout-form]');
    const currency = cart.currency || drawer.dataset.moneyCurrency || 'USD';
    const meta = protectionMeta();
    const merchandise = meta ? hasMerchandise(cart, meta.productId) : cart.items.length > 0;
    const selected = protectionSelected(cart);
    const coverage = meta ? protectionLines(cart, meta.productId) : [];
    const matchingVariant = merchandise ? closestProtectionVariant(cart, meta) : null;
    const coverageReady = !selected || !matchingVariant ||
      (coverage.length === 1 && Number(coverage[0].variant_id) === matchingVariant.id && coverage[0].quantity === 1);

    items.replaceChildren();
    if (!merchandise) {
      const empty = document.createElement('p');
      empty.className = 'bts-cart-drawer__empty';
      empty.textContent = 'Your field bag is empty.';
      items.append(empty);
    } else {
      cart.items
        .filter(item => !meta || Number(item.product_id) !== meta.productId)
        .forEach(item => items.append(makeCartItem(item, currency)));
    }

    total.textContent = money(cart.total_price, currency);
    document.querySelectorAll('[data-bts-cart-protection]').forEach(control => {
      const checkbox = control.querySelector('[data-bts-protection-checkbox]');
      const price = control.querySelector('[data-bts-protection-price]');
      control.hidden = !merchandise || !matchingVariant;
      if (checkbox) checkbox.checked = selected;
      if (price && matchingVariant) {
        const charged = selected && coverageReady ? coverage[0]?.final_line_price : null;
        price.textContent = money(charged ?? matchingVariant.price, currency);
      }
    });
    if (!merchandise) {
      const termsCheckbox = checkoutForm?.querySelector('[data-bts-cart-terms-checkbox]');
      if (termsCheckbox) termsCheckbox.checked = false;
    }
    document.querySelectorAll('[data-bts-cart-checkout]').forEach(button => {
      button.dataset.cartHasItems = String(merchandise);
      button.dataset.protectionReady = String(!protectionPricingConfigured || !meta || !merchandise || coverageReady);
      syncCartTerms(button.closest('[data-bts-cart-checkout-form]'));
    });
    updateCartCount(cart.item_count);
  };

  const syncCartTerms = form => {
    if (!form) return;
    const checkbox = form.querySelector('[data-bts-cart-terms-checkbox]');
    const checkout = form.querySelector('[data-bts-cart-checkout]');
    if (!checkbox || !checkout) return;
    const accepted = checkbox.checked;
    const hasItems = checkout.dataset.cartHasItems === 'true';
    const label = checkbox.closest('[data-bts-cart-terms]');
    label?.classList.toggle('is-invalid', !accepted);
    checkbox.setAttribute('aria-invalid', String(!accepted));
    checkout.disabled = !accepted || !hasItems || checkout.dataset.protectionReady === 'false' || pendingCartTasks > 0;
  };

  const initCartTerms = () => {
    document.querySelectorAll('[data-bts-cart-checkout-form]').forEach(syncCartTerms);
  };

  const setCartStatus = message => {
    document.querySelectorAll('[data-bts-cart-status]').forEach(status => {
      status.textContent = message;
    });
  };

  const refreshCart = async () => {
    const { cart, changed } = await queueCartTask(async () => {
      const result = await reconcileProtection(await fetchCart());
      renderCart(result.cart);
      return result;
    });
    reloadFullCartIfChanged(changed);
    return cart;
  };

  const openCart = async trigger => {
    const drawer = document.querySelector('[data-bts-cart-drawer]');
    if (!drawer) return;
    lastDialogTrigger = trigger || document.activeElement;
    setCartStatus('Loading field bag…');
    showBtsDialog(drawer);
    try {
      await refreshCart();
      setCartStatus('');
    } catch (error) {
      setCartStatus(error.message);
    }
  };

  const changeCartLine = async (lineKey, quantity) => {
    setCartStatus('Updating field bag…');
    const { changed } = await queueCartTask(async () => {
      const result = await reconcileProtection(await postCart('change', { id: lineKey, quantity }));
      renderCart(result.cart);
      return result;
    });
    reloadFullCartIfChanged(changed);
    setCartStatus('Field bag updated.');
  };

  const setProtectionPreference = async selected => {
    setCartStatus('Updating package protection…');
    const { cart } = await queueCartTask(async () => {
      const updated = await postCart('update', { attributes: { 'Package Protection': selected ? 'Yes' : 'No' } });
      const result = await reconcileProtection(updated);
      renderCart(result.cart);
      return result;
    });
    reloadFullCartIfChanged(true);
    setCartStatus(selected && hasMerchandise(cart, protectionMeta().productId) ? 'Package Protection selected.' : 'Package Protection removed.');
  };

  const addProduct = async form => {
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-bts-form-status]');
    submit.disabled = true;
    status.textContent = 'Recording artifact…';

    try {
      const response = await fetch(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'This artifact could not be added.');
      status.textContent = 'Added to field bag.';
      document.dispatchEvent(new CustomEvent('bts:add_to_cart', { detail: { variantId: data.variant_id } }));
      const panel = form.closest('dialog');
      if (panel?.open) panel.close();
      await openCart(form);
    } catch (error) {
      status.textContent = error.message;
    } finally {
      submit.disabled = false;
    }
  };

  const updateGalleryStatus = gallery => {
    const slides = [...gallery.querySelectorAll('.bts-panel__slide')];
    const status = gallery.parentElement.querySelector('[data-bts-gallery-status]');
    if (!status || slides.length < 2) return;
    const current = slides.reduce((nearest, slide, index) => {
      const distance = Math.abs(slide.offsetLeft - gallery.scrollLeft);
      return distance < nearest.distance ? { index, distance } : nearest;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    status.dataset.index = current;
    status.textContent = `${String(current + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
  };

  const moveGallery = control => {
    const media = control.closest('.bts-panel__media');
    const gallery = media?.querySelector('[data-bts-gallery]');
    const slides = [...(gallery?.querySelectorAll('.bts-panel__slide') || [])];
    const status = media?.querySelector('[data-bts-gallery-status]');
    if (!gallery || slides.length < 2 || !status) return;
    const current = Number.parseInt(status.dataset.index, 10) || 0;
    const step = control.dataset.btsGalleryControl === 'next' ? 1 : -1;
    const next = Math.min(slides.length - 1, Math.max(0, current + step));
    gallery.scrollTo({ left: slides[next].offsetLeft - gallery.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    status.dataset.index = next;
    status.textContent = `${String(next + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
  };

  const initGalleries = () => {
    document.querySelectorAll('[data-bts-gallery]').forEach(gallery => {
      if (gallery.dataset.btsGalleryReady === 'true') return;
      gallery.dataset.btsGalleryReady = 'true';
      let frame;
      gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => updateGalleryStatus(gallery));
      }, { passive: true });
    });
  };

  const initScenes = () => {
    root.classList.add('is-enhanced');
    const scenes = document.querySelectorAll('[data-bts-scene]');
    if (reducedMotion || !('IntersectionObserver' in window)) {
      scenes.forEach(scene => scene.classList.add('bts-in-view'));
      return;
    }

    const seen = new WeakSet();
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('bts-in-view');
      if (!seen.has(entry.target)) {
        seen.add(entry.target);
        document.dispatchEvent(new CustomEvent('bts:scene_view', { detail: { scene: entry.target.dataset.btsScene } }));
      }
      observer.unobserve(entry.target);
    }), { threshold: .12 });
    scenes.forEach(scene => observer.observe(scene));
  };

  const reopenCollectionPanel = () => {
    let panelId;
    try {
      panelId = window.sessionStorage.getItem('bts:reopenCollectionPanel');
    } catch (error) {
      return;
    }
    if (!panelId) return;

    const panel = document.getElementById(panelId);
    if (!panel) return;
    window.sessionStorage.removeItem('bts:reopenCollectionPanel');
    window.sessionStorage.removeItem('bts:returnCollectionPanel');
    window.sessionStorage.removeItem('bts:returnCollectionUrl');
    lastDialogTrigger = document.querySelector(`[data-dialog-target="${CSS.escape(panelId)}"]`);
    showBtsDialog(panel);
  };

  const initListeners = () => {
    if (listenersReady) return;
    listenersReady = true;

    document.addEventListener('click', event => {
      const gameLink = event.target.closest('[data-bts-game-portal]');
      if (gameLink && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0 && !gameLink.hasAttribute('download') && gameLink.target !== '_blank') {
        event.preventDefault();
        openGamePortal(gameLink);
        return;
      }

      const headerClose = event.target.closest('[data-bts-header-close]');
      if (headerClose) {
        headerClose.closest('[data-bts-header-disclosure]')?.removeAttribute('open');
        return;
      }

      const headerSummary = event.target.closest('[data-bts-header-disclosure] > summary');
      if (headerSummary) {
        document.querySelectorAll('[data-bts-header-disclosure][open]').forEach(disclosure => {
          if (disclosure !== headerSummary.parentElement) disclosure.removeAttribute('open');
        });
      } else if (!event.target.closest('[data-bts-header-disclosure]')) {
        document.querySelectorAll('[data-bts-header-disclosure][open]').forEach(disclosure => disclosure.removeAttribute('open'));
      }

      const enter = event.target.closest('.bts-button--enter');
      if (enter && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        const target = document.querySelector(enter.getAttribute('href'));
        if (target) {
          event.preventDefault();
          openJournal(enter, target);
        }
        return;
      }

      const galleryControl = event.target.closest('[data-bts-gallery-control]');
      if (galleryControl) {
        event.preventDefault();
        moveGallery(galleryControl);
        return;
      }

      const collectionProduct = event.target.closest('.bts-collection-panel__card');
      if (collectionProduct && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        const collectionPanel = collectionProduct.closest('.bts-collection-panel');
        if (collectionPanel?.id) {
          try {
            window.sessionStorage.setItem('bts:returnCollectionPanel', collectionPanel.id);
            window.sessionStorage.setItem('bts:returnCollectionUrl', window.location.href);
          } catch (error) {
            // Navigation still works when storage is unavailable.
          }
        }
      }

      const examine = event.target.closest('[data-bts-examine]');
      if (examine) {
        const panel = document.getElementById(examine.dataset.dialogTarget);
        if (panel) {
          event.preventDefault();
          lastDialogTrigger = examine;
          showBtsDialog(panel);
        }
        return;
      }

      const cartLink = event.target.closest('[data-bts-cart-link]');
      if (cartLink) {
        event.preventDefault();
        openCart(cartLink);
        return;
      }

      const remove = event.target.closest('[data-bts-cart-remove]');
      if (remove) changeCartLine(remove.dataset.lineKey, 0).catch(error => setCartStatus(error.message));

      const update = event.target.closest('[data-bts-cart-update]');
      if (update) {
        const quantity = update.parentElement.querySelector('[data-bts-cart-quantity]');
        const value = Math.max(0, Number.parseInt(quantity.value, 10) || 0);
        changeCartLine(update.dataset.lineKey, value).catch(error => setCartStatus(error.message));
      }
    });

    document.addEventListener('change', event => {
      if (event.target.matches('[data-bts-protection-checkbox]')) {
        setProtectionPreference(event.target.checked).catch(async error => {
          setCartStatus(error.message);
          try { renderCart(await fetchCart()); } catch (_refreshError) { /* Keep the original error visible. */ }
        });
        return;
      }

      if (event.target.matches('[data-bts-cart-terms-checkbox]')) {
        syncCartTerms(event.target.closest('[data-bts-cart-checkout-form]'));
        return;
      }

      if (event.target.matches('[data-bts-variant-select]')) {
        const option = event.target.selectedOptions[0];
        const panel = event.target.closest('.bts-product-panel');
        const price = panel?.querySelector('[data-bts-panel-price]');
        if (price && option?.dataset.price) price.textContent = option.dataset.price;
      }

      if (event.target.matches('[data-bts-cart-quantity]')) {
        const quantity = Math.max(0, Number.parseInt(event.target.value, 10) || 0);
        changeCartLine(event.target.dataset.lineKey, quantity).catch(error => setCartStatus(error.message));
      }
    });

    document.addEventListener('submit', event => {
      const checkoutForm = event.target.closest('[data-bts-cart-checkout-form]');
      if (checkoutForm) {
        const checkbox = checkoutForm.querySelector('[data-bts-cart-terms-checkbox]');
        if (!checkbox?.checked) {
          event.preventDefault();
          syncCartTerms(checkoutForm);
          checkbox?.focus();
        }
        return;
      }

      const form = event.target.closest('[data-bts-product-form]');
      if (!form) return;
      event.preventDefault();
      addProduct(form);
    });

    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const openDialogs = [...document.querySelectorAll('.bts-product-panel[open], .bts-cart-drawer[open]')];
      const openDialog = openDialogs.at(-1);
      if (openDialog) openDialog.close();
    });

    document.addEventListener('close', event => {
      if (!event.target.matches('dialog')) return;
      requestAnimationFrame(syncDialogScrollLock);
      if (lastDialogTrigger?.isConnected) lastDialogTrigger.focus();
    }, true);

    window.addEventListener('pageshow', () => {
      document.querySelectorAll('.bts-game-portal').forEach(overlay => overlay.remove());
      gamePortalOpening = false;
    });
  };

  const init = () => {
    initListeners();
    initScenes();
    initGalleries();
    initCartTerms();
    reopenCollectionPanel();
    refreshCart().catch(() => {});
  };

  document.addEventListener('DOMContentLoaded', init, { once: true });
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', event => {
    reopenCollectionPanel();
    syncDialogScrollLock();
    if (event.persisted) refreshCart().catch(error => setCartStatus(error.message));
  });
})();
